import React, { useMemo, useState } from 'react'
import { gql, useQuery } from '@apollo/client'
import { Bar } from 'react-chartjs-2'
import {
  Chart as ChartJS, CategoryScale, LinearScale, BarElement, LineController, LineElement, PointElement,
  Tooltip, Legend,
} from 'chart.js'
import {
  Container, Paper, Typography, Box, CircularProgress, Table, TableHead, TableRow, TableCell, TableBody,
  Chip, TextField, MenuItem, IconButton, Tabs, Tab, Alert, Button,
} from '@mui/material'
import NetworkCheckIcon from '@mui/icons-material/NetworkCheck'
import RefreshIcon from '@mui/icons-material/Refresh'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import { getEgressReport, getEgressBreakdown } from '../apollo/queries'

ChartJS.register(CategoryScale, LinearScale, BarElement, LineController, LineElement, PointElement, Tooltip, Legend)

const GET_REPORT = gql`${getEgressReport}`
const GET_BREAKDOWN = gql`${getEgressBreakdown}`

const WINDOWS = [
  { hours: 1, label: 'Last hour' },
  { hours: 6, label: 'Last 6 hours' },
  { hours: 24, label: 'Last 24 hours' },
  { hours: 48, label: 'Last 48 hours' },
  { hours: 168, label: 'Last 7 days' },
]

// Stream names are the backend's (egressMonitor.js streamOf).
const CLIENTS = 'Responses to clients'
const DATABASE = 'Database (public proxy)'
const OUTBOUND = 'Outbound API calls'
const MYSQL_ALL = 'MySQL server, all clients'
const BILLED_STREAMS = [CLIENTS, DATABASE, OUTBOUND]
const STREAM_COLORS = {
  [CLIENTS]: '#818cf8',
  [DATABASE]: '#f87171',
  [OUTBOUND]: '#fbbf24',
  [MYSQL_ALL]: '#94a3b8',
}

const KIND = {
  http_op: { label: 'API response', color: '#818cf8' },
  ws_op: { label: 'WebSocket', color: '#38bdf8' },
  db_ctx: { label: 'Database', color: '#f87171' },
  socket: { label: 'Outbound', color: '#fbbf24' },
}

const mono = { fontFamily: 'monospace', fontSize: 12.5, wordBreak: 'break-word' }

function fmtBytes(b) {
  if (!b) return '0 B'
  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  let v = b
  let i = 0
  while (v >= 1000 && i < units.length - 1) { v /= 1000; i += 1 }
  return `${v >= 100 ? v.toFixed(0) : v >= 10 ? v.toFixed(1) : v.toFixed(2)} ${units[i]}`
}
const fmtNum = (n) => Math.round(n || 0).toLocaleString()
const fmtMoney = (d) => (d > 0 && d < 0.01 ? '< $0.01' : `$${(d || 0).toFixed(2)}`)
const fmtPct = (part, whole) => (whole > 0 ? `${((part / whole) * 100).toFixed(1)}%` : '—')
function fmtDuration(sec) {
  const d = Math.floor(sec / 86400)
  const h = Math.floor((sec % 86400) / 3600)
  const m = Math.floor((sec % 3600) / 60)
  if (d) return `${d}d ${h}h`
  return h ? `${h}h ${m}m` : `${m}m`
}
function parseExtra(s) {
  if (!s) return null
  try { return JSON.parse(s) } catch (_) { return null }
}

/** Projections from the seconds tracking actually ran — redeploy gaps excluded. */
function makeRate(report) {
  const secs = report?.trackedSeconds || 0
  const price = report?.pricePerGb || 0.05
  const perDay = (bytes) => (secs > 0 ? (bytes / secs) * 86400 : 0)
  return {
    secs,
    perDay,
    dollarsPerDay: (bytes) => (perDay(bytes) / 1e9) * price,
    perMinute: (count) => (secs > 0 ? (count / secs) * 60 : 0),
  }
}

/** The bytes of a row Railway actually bills. */
function billedOf(row, dbPublic) {
  switch (row.layer) {
    case 'http_op':
    case 'http_who':
    case 'static_file':
    case 'ws_op':
    case 'ws_who':
      return row.bytesOut
    case 'db_ctx':
    case 'db_sql':
      return dbPublic ? row.bytesIn + row.bytesOut : 0
    case 'socket':
      if (row.key.startsWith('mysql · public')) return row.bytesIn + row.bytesOut
      return row.key.startsWith('out ') || row.key.startsWith('clients') ? row.bytesOut : 0
    default:
      return 0
  }
}

// ── building blocks ──────────────────────────────────────────────────────────

function StatCard({ title, bytes, rate, color, note }) {
  const perDay = rate.dollarsPerDay(bytes)
  return (
    <Paper variant="outlined" sx={{ p: 2, flex: '1 1 230px', minWidth: 230, borderLeft: `4px solid ${color}` }}>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4 }}>
        {title}
      </Typography>
      <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.5 }}>
        {fmtMoney(perDay)}
        <Typography component="span" variant="body2" color="text.secondary"> / day</Typography>
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {fmtBytes(rate.perDay(bytes))}/day · {fmtMoney(perDay * 30)} / 30 days
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
        {fmtBytes(bytes)} in this window{note ? ` · ${note}` : ''}
      </Typography>
    </Paper>
  )
}

function DataTable({ columns, rows, empty, renderDetail, rowKey, minWidth = 820 }) {
  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Table size="small" sx={{ minWidth }}>
        <TableHead>
          <TableRow>
            {renderDetail && <TableCell sx={{ width: 40 }} />}
            {columns.map((c) => <TableCell key={c.label} align={c.align}>{c.label}</TableCell>)}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 && (
            <TableRow>
              <TableCell colSpan={columns.length + 1}>
                <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>{empty}</Typography>
              </TableCell>
            </TableRow>
          )}
          {rows.map((row, i) => (
            <DataRow key={rowKey ? rowKey(row) : i} row={row} columns={columns} renderDetail={renderDetail} />
          ))}
        </TableBody>
      </Table>
    </Box>
  )
}

function DataRow({ row, columns, renderDetail }) {
  const [open, setOpen] = useState(false)
  return (
    <>
      <TableRow hover sx={{ '& td': { borderBottom: open ? 'none' : undefined }, verticalAlign: 'top' }}>
        {renderDetail && (
          <TableCell>
            <IconButton size="small" onClick={() => setOpen((o) => !o)}>
              {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
            </IconButton>
          </TableCell>
        )}
        {columns.map((c) => <TableCell key={c.label} align={c.align} sx={c.sx}>{c.render(row)}</TableCell>)}
      </TableRow>
      {renderDetail && open && (
        <TableRow>
          <TableCell colSpan={columns.length + 1} sx={{ py: 1.5, bgcolor: 'grey.50' }}>{renderDetail(row)}</TableCell>
        </TableRow>
      )}
    </>
  )
}

/** A statement's shape plus the source lines that ran it. */
function Statement({ extra }) {
  const x = parseExtra(extra)
  if (!x) return <Typography sx={mono}>—</Typography>
  return (
    <Box>
      <Typography sx={mono}>{x.sql}</Typography>
      {(x.from || []).map((f) => (
        <Typography key={f} variant="caption" sx={{ ...mono, display: 'block', color: '#475569' }}>↳ {f}</Typography>
      ))}
    </Box>
  )
}

/** Lazy detail for one row: an operation's callers, or a context's statements. */
function Breakdown({ layer, keyValue, sub, hours, rate, dbPublic }) {
  const { data, loading, error } = useQuery(GET_BREAKDOWN, {
    variables: { layer, key: keyValue, sub, hours },
    fetchPolicy: 'network-only',
  })
  if (loading) return <CircularProgress size={18} />
  if (error) return <Alert severity="error">{error.message}</Alert>
  const rows = data?.egressBreakdown ?? []

  if (layer === 'db_sql' && sub != null) {
    return (
      <DataTable
        minWidth={700}
        rows={rows}
        empty="No statements recorded for this in the window."
        columns={[
          { label: 'Statement (and where it came from)', render: (r) => <Statement extra={r.extra} /> },
          { label: 'Calls', align: 'right', render: (r) => fmtNum(r.count) },
          { label: 'Read from MySQL', align: 'right', render: (r) => fmtBytes(r.bytesIn) },
          { label: 'Avg / call', align: 'right', render: (r) => fmtBytes(r.count ? r.bytesIn / r.count : 0) },
          { label: '$ / day', align: 'right', render: (r) => fmtMoney(rate.dollarsPerDay(billedOf(r, dbPublic))) },
        ]}
      />
    )
  }
  return (
    <DataTable
      minWidth={700}
      rows={rows}
      empty="No callers recorded for this in the window."
      columns={[
        { label: layer === 'http_agent' ? 'Client type' : 'Caller · client · IP', sx: mono, render: (r) => r.sub || '—' },
        { label: 'Calls', align: 'right', render: (r) => fmtNum(r.count) },
        { label: 'Per min', align: 'right', render: (r) => rate.perMinute(r.count).toFixed(1) },
        { label: 'Sent', align: 'right', render: (r) => fmtBytes(r.bytesOut) },
        { label: '$ / day', align: 'right', render: (r) => fmtMoney(rate.dollarsPerDay(billedOf(r, dbPublic))) },
      ]}
    />
  )
}

/**
 * An operation's callers two ways: by client type, where a crawler spread over
 * hundreds of IPs still adds up to one row, and by individual caller.
 */
function OperationCallers({ opKey, hours, rate, dbPublic }) {
  const heading = { fontWeight: 700, color: '#475569', display: 'block', mb: 0.5 }
  return (
    <Box sx={{ display: 'grid', gap: 2 }}>
      <Box>
        <Typography variant="caption" sx={heading}>By client type — crawlers and scripts are named here</Typography>
        <Breakdown layer="http_agent" keyValue={opKey} hours={hours} rate={rate} dbPublic={dbPublic} />
      </Box>
      <Box>
        <Typography variant="caption" sx={heading}>By caller</Typography>
        <Breakdown layer="http_who" keyValue={opKey} hours={hours} rate={rate} dbPublic={dbPublic} />
      </Box>
    </Box>
  )
}

function HourlyChart({ series }) {
  const data = useMemo(() => {
    const hours = [...new Set(series.map((p) => p.hour))].sort()
    const byKey = new Map(series.map((p) => [`${p.hour}|${p.stream}`, p.bytes]))
    const values = (stream) => hours.map((h) => (byKey.get(`${h}|${stream}`) || 0) / 1e9)
    return {
      labels: hours.map((h) => new Date(h).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric' })),
      datasets: [
        ...BILLED_STREAMS.map((s) => ({
          type: 'bar', label: s, data: values(s), backgroundColor: STREAM_COLORS[s], stack: 'billed', borderRadius: 3,
        })),
        {
          type: 'line', label: `${MYSQL_ALL} (what MySQL sent)`, data: values(MYSQL_ALL), stack: 'server',
          borderColor: STREAM_COLORS[MYSQL_ALL], borderDash: [5, 4], pointRadius: 0, tension: 0.25,
        },
      ],
    }
  }, [series])

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    scales: {
      x: { stacked: true, ticks: { maxRotation: 0, autoSkip: true, maxTicksLimit: 12 } },
      y: { stacked: true, title: { display: true, text: 'GB per hour' } },
    },
    plugins: {
      legend: { position: 'bottom' },
      tooltip: { callbacks: { label: (ctx) => `${ctx.dataset.label}: ${fmtBytes(ctx.raw * 1e9)}` } },
    },
  }

  if (series.length === 0) {
    return <Typography variant="body2" color="text.secondary">No hourly data yet — the first bucket appears a minute after the backend starts.</Typography>
  }
  return <Box sx={{ height: 300 }}><Bar data={data} options={options} /></Box>
}

// ── page ─────────────────────────────────────────────────────────────────────

export default function EgressMonitor() {
  const [hours, setHours] = useState(48)
  const [tab, setTab] = useState(0)

  const { data, loading, error, refetch } = useQuery(GET_REPORT, {
    variables: { hours },
    fetchPolicy: 'network-only',
    // Slow on purpose: this page's own responses are egress too, and it shows
    // up in its own table as EgressReport.
    pollInterval: 5 * 60 * 1000,
    notifyOnNetworkStatusChange: true,
  })

  const report = data?.egressReport
  const rate = useMemo(() => makeRate(report), [report])
  const dbPublic = !!report?.dbPublic

  const view = useMemo(() => {
    if (!report) return null
    const layer = (name) => report.rows.filter((r) => r.layer === name)
    const total = (name) => report.layerTotals.find((t) => t.layer === name) || { bytesOut: 0, bytesIn: 0, count: 0 }
    const stream = (name) => report.streams.find((s) => s.stream === name) || { bytesOut: 0, bytesIn: 0, billedBytes: 0, connections: 0 }

    const sockets = layer('socket')
    const mysqlSockets = sockets.filter((r) => r.key.startsWith('mysql'))
    const outbound = sockets.filter((r) => r.key.startsWith('out '))

    // One ranked list across every stream: the answer to "what do I fix first".
    const culprits = [...layer('http_op'), ...layer('ws_op'), ...layer('db_ctx'), ...outbound]
      .map((r) => ({ ...r, billed: billedOf(r, dbPublic) }))
      .filter((r) => r.billed > 0)
      .sort((a, b) => b.billed - a.billed)
      .slice(0, 40)

    const billedTotal = BILLED_STREAMS.reduce((sum, s) => sum + stream(s).billedBytes, 0)
    const clientsTruth = stream(CLIENTS).bytesOut
    const clientsLabelled = total('http_op').bytesOut + total('ws_op').bytesOut
    const dbTruth = mysqlSockets.reduce((sum, r) => sum + r.bytesIn, 0)
    const dbLabelled = total('db_ctx').bytesIn

    return {
      layer, total, stream, culprits, billedTotal, mysqlSockets, sockets,
      clientsTruth, clientsLabelled, dbTruth, dbLabelled,
    }
  }, [report, dbPublic])

  const header = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1, flexWrap: 'wrap' }}>
      <NetworkCheckIcon color="primary" />
      <Typography variant="h5" sx={{ fontWeight: 700, flex: 1 }}>Egress Monitor</Typography>
      <TextField select size="small" label="Window" value={hours} onChange={(e) => setHours(Number(e.target.value))} sx={{ width: 170 }}>
        {WINDOWS.map((w) => <MenuItem key={w.hours} value={w.hours}>{w.label}</MenuItem>)}
      </TextField>
      <Button variant="outlined" size="small" startIcon={<RefreshIcon />} onClick={() => refetch()} disabled={loading}>
        Refresh
      </Button>
    </Box>
  )

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 6 }}>
      <Paper sx={{ p: 4 }}>
        {header}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Every byte Railway can bill this backend for, and what caused it: responses to the apps and
          websites (by operation and by caller), WebSocket pushes, the database connection, and calls out to
          other services. Counted inside the backend and saved every minute, so the numbers survive redeploys.
          Expand a row to see who is calling it or which SQL it runs.
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error.message}</Alert>}
        {!report && loading && <CircularProgress size={24} />}

        {report && !report.enabled && (
          <Alert severity="warning">Tracking is off on the backend (EGRESS_TRACKING=off).</Alert>
        )}

        {report && report.enabled && view && (
          <>
            <Alert severity={dbPublic ? 'error' : 'success'} sx={{ mb: 2 }}>
              {dbPublic ? (
                <>
                  <strong>The backend reaches MySQL through Railway&apos;s public proxy.</strong> Every byte in the
                  Database figures below is billed as egress — once on the MySQL service for what it sends back, once
                  here for the SQL we send. Pointing DB_HOST at the private host (<code>mysql.railway.internal</code>,
                  port 3306) makes that whole stream free.
                </>
              ) : (
                <>The backend reaches MySQL over the private network, so database traffic is not billed.</>
              )}
            </Alert>

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              Tracked {fmtDuration(rate.secs)} of this window
              {report.firstTrackedAt ? ` · data since ${new Date(report.firstTrackedAt).toLocaleString()}` : ''}
              {` · ${fmtNum(report.processStarts)} backend start${report.processStarts === 1 ? '' : 's'} (deploys/restarts)`}
              {` · updated ${new Date(report.generatedAt).toLocaleTimeString()}`}
              {` · $${report.pricePerGb}/GB`}
            </Typography>
            {rate.secs < 3600 && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Less than an hour of data so far. Per-day projections will settle as more comes in — check back after a
                full day to catch overnight polling.
              </Alert>
            )}

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
              <StatCard title="Total billed egress" bytes={view.billedTotal} rate={rate} color="#0f172a" />
              <StatCard
                title="Responses to clients" bytes={view.stream(CLIENTS).billedBytes} rate={rate} color={STREAM_COLORS[CLIENTS]}
                note={`labels explain ${fmtPct(view.clientsLabelled, view.clientsTruth)}`}
              />
              <StatCard
                title={dbPublic ? 'Database (public proxy)' : 'Database (free)'} bytes={view.stream(DATABASE).billedBytes}
                rate={rate} color={STREAM_COLORS[DATABASE]}
                note={`${fmtBytes(view.dbTruth)} read · labels explain ${fmtPct(view.dbLabelled, view.dbTruth)}`}
              />
              <StatCard title="Outbound API calls" bytes={view.stream(OUTBOUND).billedBytes} rate={rate} color={STREAM_COLORS[OUTBOUND]} />
            </Box>

            <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Billed egress per hour</Typography>
              <HourlyChart series={report.series} />
            </Paper>

            <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
              <Tab label="Biggest costs" />
              <Tab label="API operations" />
              <Tab label="Callers" />
              <Tab label="Database" />
              <Tab label="WebSocket" />
              <Tab label="Network & files" />
              <Tab label="MySQL server" />
            </Tabs>

            {tab === 0 && (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  Everything that costs money, ranked. The same feature can appear twice — once for the response it sends
                  to the app and once for the database reads behind it — because they are billed separately.
                </Typography>
                <DataTable
                  rows={view.culprits}
                  rowKey={(r) => `${r.layer}|${r.key}`}
                  empty="Nothing billed recorded in this window yet."
                  renderDetail={(r) => {
                    if (r.layer === 'http_op') return <OperationCallers opKey={r.key} hours={hours} rate={rate} dbPublic={dbPublic} />
                    if (r.layer === 'ws_op') return <Breakdown layer="ws_who" keyValue={r.key} hours={hours} rate={rate} dbPublic={dbPublic} />
                    if (r.layer === 'db_ctx') return <Breakdown layer="db_sql" sub={r.key} hours={hours} rate={rate} dbPublic={dbPublic} />
                    return <Typography variant="body2" color="text.secondary">Bytes this backend sent to {r.key.slice(4)}.</Typography>
                  }}
                  columns={[
                    { label: 'Kind', render: (r) => <Chip size="small" label={KIND[r.layer].label} sx={{ bgcolor: KIND[r.layer].color, color: '#0f172a', fontWeight: 600 }} /> },
                    { label: 'What', sx: mono, render: (r) => r.key },
                    { label: 'Calls', align: 'right', render: (r) => (r.layer === 'socket' ? '—' : fmtNum(r.count)) },
                    { label: 'Billed in window', align: 'right', render: (r) => fmtBytes(r.billed) },
                    { label: 'Per day', align: 'right', render: (r) => fmtBytes(rate.perDay(r.billed)) },
                    { label: '$ / day', align: 'right', render: (r) => <strong>{fmtMoney(rate.dollarsPerDay(r.billed))}</strong> },
                    { label: 'Share', align: 'right', render: (r) => fmtPct(r.billed, view.billedTotal) },
                  ]}
                />
              </>
            )}

            {tab === 1 && (
              <DataTable
                rows={view.layer('http_op')}
                rowKey={(r) => r.key}
                empty="No responses recorded in this window yet."
                renderDetail={(r) => <OperationCallers opKey={r.key} hours={hours} rate={rate} dbPublic={dbPublic} />}
                columns={[
                  { label: 'Operation / route', sx: mono, render: (r) => r.key },
                  { label: 'Calls', align: 'right', render: (r) => fmtNum(r.count) },
                  { label: 'Per min', align: 'right', render: (r) => rate.perMinute(r.count).toFixed(1) },
                  { label: 'Sent', align: 'right', render: (r) => fmtBytes(r.bytesOut) },
                  { label: 'Avg / call', align: 'right', render: (r) => fmtBytes(r.count ? r.bytesOut / r.count : 0) },
                  { label: 'Per day', align: 'right', render: (r) => fmtBytes(rate.perDay(r.bytesOut)) },
                  { label: '$ / day', align: 'right', render: (r) => fmtMoney(rate.dollarsPerDay(r.bytesOut)) },
                  { label: 'Share', align: 'right', render: (r) => fmtPct(r.bytesOut, view.total('http_op').bytesOut) },
                ]}
              />
            )}

            {tab === 2 && (
              <>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>By client type</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  App, website or crawler, without the account or IP — a bot spread over hundreds of addresses still
                  adds up to one row. Crawlers and scripts are named from their user agent.
                </Typography>
                <DataTable
                  rows={view.layer('http_agent')}
                  rowKey={(r) => `${r.key}|${r.sub}`}
                  empty="No client types recorded in this window yet."
                  columns={[
                    { label: 'Client type', sx: mono, render: (r) => r.sub },
                    { label: 'Operation', sx: mono, render: (r) => r.key },
                    { label: 'Calls', align: 'right', render: (r) => fmtNum(r.count) },
                    { label: 'Per min', align: 'right', render: (r) => rate.perMinute(r.count).toFixed(1) },
                    { label: 'Sent', align: 'right', render: (r) => fmtBytes(r.bytesOut) },
                    { label: '$ / day', align: 'right', render: (r) => fmtMoney(rate.dollarsPerDay(r.bytesOut)) },
                  ]}
                />
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 4, mb: 1 }}>By caller</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  Each device or browser and what it downloads. A tablet or POS left on overnight shows up here with a
                  steady calls-per-minute rate. The account comes from the login token; the IP tells devices apart.
                </Typography>
                <DataTable
                  rows={view.layer('http_who')}
                  rowKey={(r) => `${r.key}|${r.sub}`}
                  empty="No callers recorded in this window yet."
                  columns={[
                    { label: 'Caller · client · IP', sx: mono, render: (r) => r.sub },
                    { label: 'Operation', sx: mono, render: (r) => r.key },
                    { label: 'Calls', align: 'right', render: (r) => fmtNum(r.count) },
                    { label: 'Per min', align: 'right', render: (r) => rate.perMinute(r.count).toFixed(1) },
                    { label: 'Sent', align: 'right', render: (r) => fmtBytes(r.bytesOut) },
                    { label: '$ / day', align: 'right', render: (r) => fmtMoney(rate.dollarsPerDay(r.bytesOut)) },
                  ]}
                />
              </>
            )}

            {tab === 3 && (
              <>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>By feature (the request or background job that ran the SQL)</Typography>
                <DataTable
                  rows={view.layer('db_ctx')}
                  rowKey={(r) => r.key}
                  empty="No database traffic recorded in this window yet."
                  renderDetail={(r) => <Breakdown layer="db_sql" sub={r.key} hours={hours} rate={rate} dbPublic={dbPublic} />}
                  columns={[
                    { label: 'Request or job', sx: mono, render: (r) => r.key },
                    { label: 'Queries', align: 'right', render: (r) => fmtNum(r.count) },
                    { label: 'Read from MySQL', align: 'right', render: (r) => fmtBytes(r.bytesIn) },
                    { label: 'SQL sent', align: 'right', render: (r) => fmtBytes(r.bytesOut) },
                    { label: 'Avg read / query', align: 'right', render: (r) => fmtBytes(r.count ? r.bytesIn / r.count : 0) },
                    { label: 'Per day', align: 'right', render: (r) => fmtBytes(rate.perDay(r.bytesIn + r.bytesOut)) },
                    { label: '$ / day', align: 'right', render: (r) => fmtMoney(rate.dollarsPerDay(billedOf(r, dbPublic))) },
                    { label: 'Share', align: 'right', render: (r) => fmtPct(r.bytesIn + r.bytesOut, view.total('db_ctx').bytesIn + view.total('db_ctx').bytesOut) },
                  ]}
                />
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 4, mb: 1 }}>Heaviest statements</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  Each SQL shape with its values removed, the source lines that issued it, and which feature ran it.
                </Typography>
                <DataTable
                  rows={view.layer('db_sql')}
                  rowKey={(r) => `${r.key}|${r.sub}`}
                  empty="No statements recorded in this window yet."
                  columns={[
                    { label: 'Statement (and where it came from)', render: (r) => <Statement extra={r.extra} /> },
                    { label: 'Feature', sx: mono, render: (r) => r.sub },
                    { label: 'Calls', align: 'right', render: (r) => fmtNum(r.count) },
                    { label: 'Read', align: 'right', render: (r) => fmtBytes(r.bytesIn) },
                    { label: 'Avg / call', align: 'right', render: (r) => fmtBytes(r.count ? r.bytesIn / r.count : 0) },
                    { label: '$ / day', align: 'right', render: (r) => fmtMoney(rate.dollarsPerDay(billedOf(r, dbPublic))) },
                  ]}
                />
              </>
            )}

            {tab === 4 && (
              <DataTable
                rows={view.layer('ws_op')}
                rowKey={(r) => r.key}
                empty="No WebSocket traffic recorded in this window yet."
                renderDetail={(r) => <Breakdown layer="ws_who" keyValue={r.key} hours={hours} rate={rate} dbPublic={dbPublic} />}
                columns={[
                  { label: 'Subscription / frame', sx: mono, render: (r) => r.key },
                  { label: 'Frames', align: 'right', render: (r) => fmtNum(r.count) },
                  { label: 'Sent', align: 'right', render: (r) => fmtBytes(r.bytesOut) },
                  { label: 'Avg / frame', align: 'right', render: (r) => fmtBytes(r.count ? r.bytesOut / r.count : 0) },
                  { label: 'Per day', align: 'right', render: (r) => fmtBytes(rate.perDay(r.bytesOut)) },
                  { label: '$ / day', align: 'right', render: (r) => fmtMoney(rate.dollarsPerDay(r.bytesOut)) },
                ]}
              />
            )}

            {tab === 5 && (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  Raw byte counters on every network connection this backend holds — the ground truth the labelled tables
                  are checked against. Loopback and private-network traffic is free.
                </Typography>
                <DataTable
                  rows={view.sockets}
                  rowKey={(r) => r.key}
                  empty="No connections recorded in this window yet."
                  columns={[
                    { label: 'Connection', sx: mono, render: (r) => r.key },
                    { label: 'Opened', align: 'right', render: (r) => fmtNum(r.count) },
                    { label: 'Sent', align: 'right', render: (r) => fmtBytes(r.bytesOut) },
                    { label: 'Received', align: 'right', render: (r) => fmtBytes(r.bytesIn) },
                    { label: 'Billed', align: 'right', render: (r) => fmtBytes(billedOf(r, dbPublic)) },
                    { label: '$ / day', align: 'right', render: (r) => fmtMoney(rate.dollarsPerDay(billedOf(r, dbPublic))) },
                  ]}
                />
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 4, mb: 1 }}>Files served from /uploads</Typography>
                <DataTable
                  rows={view.layer('static_file')}
                  rowKey={(r) => r.key}
                  empty="No /uploads files served in this window."
                  columns={[
                    { label: 'File', sx: mono, render: (r) => r.key },
                    { label: 'Requests', align: 'right', render: (r) => fmtNum(r.count) },
                    { label: 'Sent', align: 'right', render: (r) => fmtBytes(r.bytesOut) },
                    { label: 'Per day', align: 'right', render: (r) => fmtBytes(rate.perDay(r.bytesOut)) },
                    { label: '$ / day', align: 'right', render: (r) => fmtMoney(rate.dollarsPerDay(r.bytesOut)) },
                  ]}
                />
              </>
            )}

            {tab === 6 && <MysqlTab report={report} view={view} rate={rate} />}
          </>
        )}
      </Paper>
    </Container>
  )
}

function MysqlTab({ report, view, rate }) {
  const m = report.mysql
  const serverSent = view.stream(MYSQL_ALL).bytesOut
  const unexplained = Math.max(0, serverSent - view.dbTruth)
  const sinceStartPerDay = m.uptimeSeconds > 0 ? (m.bytesSentSinceStart / m.uptimeSeconds) * 86400 : 0

  return (
    <>
      {m.note && <Alert severity="warning" sx={{ mb: 2 }}>{m.note}</Alert>}

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
        <Paper variant="outlined" sx={{ p: 2, flex: '1 1 300px' }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase' }}>Since MySQL last started</Typography>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {m.bytesSentSinceStart != null ? fmtBytes(m.bytesSentSinceStart) : '—'} sent
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {m.uptimeSeconds != null ? `over ${fmtDuration(m.uptimeSeconds)} · about ${fmtBytes(sinceStartPerDay)}/day to all clients` : 'uptime unavailable'}
          </Typography>
          <Typography variant="caption" color="text.secondary">MySQL&apos;s own counter — needs no tracking history, so it is right from the first load.</Typography>
        </Paper>
        <Paper variant="outlined" sx={{ p: 2, flex: '1 1 300px' }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase' }}>This window</Typography>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>{fmtBytes(serverSent)} sent by MySQL</Typography>
          <Typography variant="body2" color="text.secondary">
            this backend read {fmtBytes(view.dbTruth)} of it · {fmtBytes(unexplained)} went to other clients
          </Typography>
          <Typography variant="caption" color="text.secondary">
            A large remainder means something else is reading the database — another service, a second deployment, or a
            desktop tool left connected. Check the client list below.
          </Typography>
        </Paper>
      </Box>

      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Bytes sent by client host (this window)</Typography>
      <DataTable
        minWidth={600}
        rows={view.layer('mysql_host')}
        rowKey={(r) => r.key}
        empty="Not available — needs performance_schema on the MySQL server."
        columns={[
          { label: 'Host', sx: mono, render: (r) => <>{r.key}{r.key === m.ownHost && <Chip size="small" label="this backend" sx={{ ml: 1 }} />}</> },
          { label: 'Sent', align: 'right', render: (r) => fmtBytes(r.bytesOut) },
          { label: 'Per day', align: 'right', render: (r) => fmtBytes(rate.perDay(r.bytesOut)) },
        ]}
      />

      <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 4, mb: 1 }}>Connected right now</Typography>
      <DataTable
        minWidth={600}
        rows={m.clients}
        rowKey={(r) => `${r.host}|${r.user}|${r.db}`}
        empty="Process list unavailable."
        columns={[
          { label: 'Host', sx: mono, render: (r) => <>{r.host || '—'}{r.isThisBackend && <Chip size="small" label="this backend" sx={{ ml: 1 }} />}</> },
          { label: 'User', sx: mono, render: (r) => r.user || '—' },
          { label: 'Database', sx: mono, render: (r) => r.db || '—' },
          { label: 'Connections', align: 'right', render: (r) => r.connections },
          { label: 'Busy', align: 'right', render: (r) => r.busy },
        ]}
      />

      <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 4, mb: 1 }}>Statements that returned the most rows (since MySQL started)</Typography>
      <DataTable
        rows={m.digests}
        rowKey={(r) => r.digest}
        empty="Not available — needs performance_schema on the MySQL server."
        columns={[
          { label: 'Statement', sx: mono, render: (r) => r.digest },
          { label: 'Calls', align: 'right', render: (r) => fmtNum(r.calls) },
          { label: 'Rows sent', align: 'right', render: (r) => fmtNum(r.rowsSent) },
          { label: 'Rows / call', align: 'right', render: (r) => fmtNum(r.calls ? r.rowsSent / r.calls : 0) },
          { label: 'Rows examined', align: 'right', render: (r) => fmtNum(r.rowsExamined) },
          { label: 'Time', align: 'right', render: (r) => `${fmtNum(r.seconds)}s` },
        ]}
      />
    </>
  )
}
